import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { cn } from 'src/utils/utils';
import { Typography } from './typography';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: boolean;
  /** Character limit shown in the counter (counts characters, not words). */
  maxWords?: number;
  helperText?: string;
  /** Extra classes for the wrapper around label + textarea. */
  containerClassName?: string;
  /** Grow the textarea to fit its content. Turn off to let a fixed or flex height win. */
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      error,
      label,
      maxWords,
      helperText,
      value,
      containerClassName,
      autoResize = true,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const internalRef = React.useRef<HTMLTextAreaElement>(null);

    React.useImperativeHandle(
      ref,
      () => internalRef.current as HTMLTextAreaElement
    );
    const wordCount = React.useMemo(() => {
      const text = typeof value === 'string' ? value.trim() : '';
      return text ? text.length : 0;
    }, [value]);

    const isOverLimit = maxWords ? wordCount > maxWords : false;

    React.useLayoutEffect(() => {
      const textarea = internalRef.current;
      if (!autoResize) {
        if (textarea) {
          textarea.style.height = '';
        }
        return;
      }
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value, autoResize]);

    return (
      <div
        className={cn(
          'tw:flex tw:flex-col tw:h-auto tw:w-full',
          label && error && 'tw:gap-s ',
          containerClassName
        )}
      >
        {label && (
          <Typography
            variant="label"
            colorClass="tw:text-text-secondary tw:mb-s"
          >
            {label}
          </Typography>
        )}

        <textarea
          ref={internalRef}
          value={value}
          className={cn(
            `
            tw:flex tw:w-full tw:resize-none tw:bg-primary tw:px-l tw:py-s
            tw:text-md tw:font-regular tw:leading-md tw:tracking-0
            tw:placeholder:text-text-secondary
            tw:outline tw:outline-solid tw:outline-border-primary
            tw:rounded-bw-2xl-3xl tw:text-text-primary
            tw:hover:outline-border-primary-hover
            tw:active:outline-border-brand
            tw:has-focus-visible:outline-border-brand
            tw:min-h-3xl
            `,
            props.disabled &&
              'tw:pointer-events-none tw:bg-disabled tw:outline-border-disabled tw:text-text-disabled',
            (error || isOverLimit) &&
              'tw:outline-border-error tw:has-focus-visible:outline-border-error',
            className
          )}
          {...props}
        />

        {(helperText || maxWords) && (
          <div className="tw:flex tw:justify-between tw:items-start tw:gap-m">
            {helperText && (
              <Typography
                variant="h5"
                className={cn(
                  error || isOverLimit
                    ? 'tw:text-text-error'
                    : 'tw:text-text-tertiary'
                )}
              >
                {helperText}
              </Typography>
            )}

            {maxWords && (
              <Typography
                variant="h6"
                className={cn(
                  'tw:ml-auto',
                  isOverLimit ? 'tw:text-text-error' : 'tw:text-text-tertiary'
                )}
              >
                {t(T.charactersCount, { current: wordCount, max: maxWords })}
              </Typography>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
